// @ts-nocheck
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CTA from "@/components/sections/CTA";
import { api } from "@/lib/api";
import { INSIGHT_POSTS } from "@/lib/content";

interface Post { slug: string; title: string; excerpt: string; content: string; tags: string[]; author: string; created_at: string; }

// Minimal markdown renderer (h2, h3, bold, lists, paragraphs)
function renderMarkdown(md: string) {
  const lines = md.split("\n");
  const out: JSX.Element[] = [];
  let inList = false; let listItems: string[] = [];
  const flushList = () => {
    if (inList) {
      out.push(<ul key={out.length} className="list-disc list-inside space-y-1.5 text-edx-muted ml-2 my-3">{listItems.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: li.replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>") }} />)}</ul>);
      inList = false; listItems = [];
    }
  };
  for (const line of lines) {
    if (line.startsWith("### ")) { flushList(); out.push(<h3 key={out.length} className="display font-bold text-xl text-white mt-6 mb-2">{line.slice(4)}</h3>); }
    else if (line.startsWith("## ")) { flushList(); out.push(<h2 key={out.length} className="display font-bold text-2xl text-white mt-8 mb-3">{line.slice(3)}</h2>); }
    else if (line.startsWith("- ")) { inList = true; listItems.push(line.slice(2)); }
    else if (line.trim() === "") { flushList(); }
    else {
      flushList();
      const html = line.replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>").replace(/\[(.+?)\]\((.+?)\)/g, "<a href='$2' class='text-edx-cyan hover:text-edx-pink'>$1</a>");
      out.push(<p key={out.length} className="text-edx-muted leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: html }} />);
    }
  }
  flushList();
  return out;
}

export default function BlogPost({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const fallback = INSIGHT_POSTS.find((item) => item.slug === slug);
    api.get(`/blog/${slug}`)
      .then((response) => {
        if (response.data && typeof response.data === "object" && response.data.slug) {
          setPost(response.data);
        } else if (fallback) {
          setPost(fallback);
        } else {
          setErr(true);
        }
      })
      .catch(() => fallback ? setPost(fallback) : setErr(true));
  }, [slug]);

  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="pt-28 pb-12 page-fade" data-testid="blog-post-page">
        <div className="container-edx pt-12 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-edx-muted hover:text-edx-cyan">
            <ArrowLeft size={14} /> All insights
          </Link>
          {err && <div className="mt-10 text-edx-muted">Post not found.</div>}
          {post && (
            <>
              <div className="mt-8 flex flex-wrap gap-1.5">
                {post.tags?.map((t) => (
                  <span key={t} className="text-[10px] font-mono uppercase tracking-widest text-edx-cyan px-2 py-0.5 rounded-full border border-edx-cyan/30 bg-edx-cyan/5">{t}</span>
                ))}
              </div>
              <h1 className="display mt-5 text-3xl md:text-5xl text-white">{post.title}</h1>
              <div className="mt-4 flex items-center gap-4 text-xs text-edx-muted font-mono">
                <span className="flex items-center gap-1"><User size={12} /> {post.author}</span>
                <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString("en-ZA")}</span>
              </div>
              <div className="mt-10 text-base">
                {renderMarkdown(post.content)}
              </div>
            </>
          )}
        </div>
        <CTA />
      </main>
      <Footer />
    </>
  );
}
